import React, { useState, useEffect, useRef } from 'react';
import Page from "../UI/Elements/Page";
import { printVoucher } from '../../api/services/order';
import { Loader } from 'semantic-ui-react';
import { UseErrorContext } from '../../global/contexts';
import styled from "styled-components";

const Div = styled.div`
  .page__header h1 {
    text-align: center;
    margin-bottom: 1rem;
  }

  .page__inner {
    background: #fff;
    border: 1px solid rgba(61, 30, 92, 0.06);
    border-radius: 24px;
    box-shadow: 0 18px 50px rgba(61, 30, 92, 0.08);
    padding: 2rem 2.25rem;
    max-width: 640px;
    margin: 0 auto;
    text-align: center;
  }

  .success-message {
    margin: 0;
  }

  .success-message p {
    margin: 0.5rem 0;
    font-family: 'Inter', sans-serif;
    color: var(--color-text-soft);
    font-size: 1.05rem;
  }

  .success-message p:first-child {
    font-family: 'Fraunces', serif;
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--color-plum);
    margin-bottom: 0.75rem;
  }

  .footer {
    margin: 2rem auto 0;
    max-width: 640px;
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .footer a {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.7rem 1.4rem;
    border-radius: 999px;
    text-decoration: none;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
  }

  .footer a:hover {
    transform: translateY(-1px);
  }

  .footer .btn-download {
    background: var(--color-coral);
    color: #fff;
    box-shadow: 0 8px 22px rgba(232, 72, 79, 0.28);
  }

  .footer .btn-download:hover {
    background: var(--color-coral-deep);
    box-shadow: 0 12px 28px rgba(232, 72, 79, 0.38);
  }

  .footer .btn-open {
    background: #fff;
    color: var(--color-plum);
    border: 1.5px solid rgba(61, 30, 92, 0.12);
  }

  .footer .btn-open:hover {
    border-color: var(--color-coral);
    color: var(--color-coral);
  }
`;

const Success = () => {
  const [loading, setLoading] = useState(false);
  const [unique, setUnique] = useState("");
  const [pdfReady, setPdfReady] = useState(false);
  const { updateError } = UseErrorContext();
  const pdfBlobUrl = useRef(null);

  useEffect(() => {
    const init = async () => {
      localStorage.setItem('items', JSON.stringify([]));
      localStorage.setItem('messages', JSON.stringify([]));
      localStorage.setItem('customer', JSON.stringify({}));

      const u = JSON.parse(localStorage.getItem('unique')) ?? "";
      setUnique(u);

      if (u !== '') {
        await _fetchAndOpenPdf(u);
      }
    };

    init();

    return () => {
      if (pdfBlobUrl.current) {
        URL.revokeObjectURL(pdfBlobUrl.current);
      }
    };
  }, []);

  const _ensurePdfBlob = async (u) => {
    if (pdfBlobUrl.current) return pdfBlobUrl.current;

    const { data } = await printVoucher(u);
    const blob = new Blob([data], { type: 'application/pdf' });
    pdfBlobUrl.current = URL.createObjectURL(blob);
    setPdfReady(true);
    return pdfBlobUrl.current;
  };

  const _fetchAndOpenPdf = async (u) => {
    try {
      setLoading(true);
      const url = await _ensurePdfBlob(u);
      window.open(url, '_blank');
    } catch (err) {
      updateError(err);
    } finally {
      setLoading(false);
    }
  };

  const _downloadVoucher = async (u) => {
    try {
      setLoading(true);
      const url = await _ensurePdfBlob(u);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cadeaubon.pdf';
      a.click();
    } catch (err) {
      updateError(err);
    } finally {
      setLoading(false);
    }
  };

  const _openVoucher = async (u) => {
    try {
      setLoading(true);
      const url = await _ensurePdfBlob(u);
      window.open(url, '_blank');
    } catch (err) {
      updateError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Div>
      <Page noBackground>
        <div className="page__header">
          <h1>Bedankt voor uw aankoop!</h1>
        </div>
        <div className="page__inner">
          {loading ? (
            <Loader active inline="centered" />
          ) : (
            <div className="success-message">
              <p>Uw betaling is geslaagd.</p>
              {pdfReady ? (
                <p>Uw cadeaubon(nen) is geopend in een nieuw tabblad. U ontvangt ook een bevestiging per e-mail.</p>
              ) : (
                <p>U krijgt zo dadelijk een e-mail met uw orderbevestiging.</p>
              )}
            </div>
          )}
        </div>
        {unique !== "" && (
          <div className="footer">
            <a className="btn-download" onClick={() => _downloadVoucher(unique)} href="#">
              ⬇ Cadeaubon(nen) downloaden
            </a>
            <a className="btn-open" onClick={() => _openVoucher(unique)} href="#">
              ↗ Openen in nieuw tabblad
            </a>
          </div>
        )}
      </Page>
    </Div>
  );
};

export default Success;
